import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useApi } from '../lib/api';
import { useAuth } from '../contexts/useAuth';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
    ArrowLeft,
    Clock,
    Calendar,
    Users,
    Building2,
    FileText,
    TrendingUp,
    Loader2,
    ExternalLink,
    Pencil,
    Check,
    X,
    UserCog,
    UserPlus,
    Trash2,
    Package,
    Link2,
    Link2Off,
    Search,
    Lock,
} from 'lucide-react';
import { cn, hoursToDays, daysToHours } from '../lib/utils';
import {
    PROJECT_STATUS_CHOICES,
    projectStatusBadgeClass,
    projectStatusLabel,
} from '../lib/project-status';
import type {
    ApiResponse,
    Project,
    TimeEntry,
    ProjectMember,
    ProjectCdp,
    ProjectOrderLine,
    User,
} from '../types';

interface ProjectStats {
    totalHours: number;
    approvedHours: number;
    billedHours: number;
    pendingHours: number;
    monthlyBreakdown: {
        month: string;
        hours: number;
    }[];
}

/** Ce que la suppression emporterait, et ce qui l'empêche. Calculé par l'API. */
export interface ProjectDeletionImpact {
    entriesByStatus: Record<string, number>;
    entriesTotal: number;
    entriesRemovable: number;
    entriesLocked: number;
    members: number;
    cdps: number;
    orderLines: number;
    notifications: number;
    sharedWith: number;
    /** Vrai si une source externe recréera le projet. */
    comesBack: boolean;
    /** Motif du refus, en français, ou null si la suppression est possible. */
    blockedReason: string | null;
}

interface ProjectDetailData extends Project {
    members: ProjectMember[];
    cdps: ProjectCdp[];
    orderLines: ProjectOrderLine[];
    /** Autres projets rattachés à la même commande. */
    sharedWith: Array<{ id: string; code: string; name: string }>;
    recentEntries: TimeEntry[];
    stats: ProjectStats;
    /** Absent pour un non-administrateur : lui ne peut pas supprimer. */
    deletion?: ProjectDeletionImpact | null;
}

export function ProjectDetail() {
    const { id } = useParams();
    const api = useApi();
    const queryClient = useQueryClient();
    const { isManager, isAdmin } = useAuth();

    const { data: projectData, isLoading } = useQuery({
        queryKey: ['project', id],
        queryFn: () => api.get<ApiResponse<ProjectDetailData>>(`/api/projects/${id}`),
        enabled: !!id,
    });

    const project = projectData?.data;

    const [isEditingBudget, setIsEditingBudget] = useState(false);
    const [budgetDaysInput, setBudgetDaysInput] = useState('');
    const [budgetError, setBudgetError] = useState<string | null>(null);

    const updateBudgetMutation = useMutation({
        mutationFn: (budgetHours: number | null) =>
            api.patch<ApiResponse<unknown>>(`/api/projects/${id}`, { budgetHours }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['project', id] });
            queryClient.invalidateQueries({ queryKey: ['projects'] });
            setIsEditingBudget(false);
            setBudgetError(null);
        },
        onError: (err: Error) => {
            setBudgetError(err.message || 'Erreur lors de la mise à jour');
        },
    });

    const [isEditingPeriod, setIsEditingPeriod] = useState(false);
    const [startDateInput, setStartDateInput] = useState('');
    const [endDateInput, setEndDateInput] = useState('');
    const [periodError, setPeriodError] = useState<string | null>(null);

    const updatePeriodMutation = useMutation({
        mutationFn: (period: { startDate: string | null; endDate: string | null }) =>
            api.patch<ApiResponse<unknown>>(`/api/projects/${id}`, period),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['project', id] });
            queryClient.invalidateQueries({ queryKey: ['projects'] });
            setIsEditingPeriod(false);
            setPeriodError(null);
        },
        onError: (err: Error) => {
            setPeriodError(err.message || 'Erreur lors de la mise à jour');
        },
    });

    const [isEditingCode, setIsEditingCode] = useState(false);
    const [codeInput, setCodeInput] = useState('');
    const [codeError, setCodeError] = useState<string | null>(null);

    const updateCodeMutation = useMutation({
        mutationFn: (code: string) =>
            api.patch<ApiResponse<{ codeLocked?: boolean }>>(`/api/projects/${id}`, { code }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['project', id] });
            queryClient.invalidateQueries({ queryKey: ['projects'] });
            setIsEditingCode(false);
            setCodeError(null);
        },
        onError: (err: Error) => setCodeError(err.message || 'Erreur lors de la mise à jour'),
    });

    const saveCode = () => {
        const code = codeInput.trim();
        if (!code) {
            setCodeError('Le code ne peut pas être vide');
            return;
        }
        if (code === project?.code) {
            setIsEditingCode(false);
            setCodeError(null);
            return;
        }
        updateCodeMutation.mutate(code);
    };

    const [isEditingStatus, setIsEditingStatus] = useState(false);
    const [statusError, setStatusError] = useState<string | null>(null);
    const [closingNotice, setClosingNotice] = useState<number | null>(null);

    const updateStatusMutation = useMutation({
        mutationFn: (status: string) =>
            api.patch<ApiResponse<{ pendingEntries?: number; closed?: boolean }>>(
                `/api/projects/${id}`,
                { status }
            ),
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: ['project', id] });
            queryClient.invalidateQueries({ queryKey: ['projects'] });
            // Les saisies en attente au moment de la clôture ne sont pas touchées, mais
            // il faut le dire : le projet n'est pas soldé pour autant.
            setClosingNotice(
                res.data?.closed && res.data?.pendingEntries ? res.data.pendingEntries : null
            );
            setIsEditingStatus(false);
            setStatusError(null);
        },
        onError: (err: Error) => {
            setStatusError(err.message || 'Erreur lors du changement de statut');
        },
    });

    // Même conversion que l'affichage juste au-dessus, de façon que le champ montre
    // toujours la date que l'utilisateur vient de lire. Une colonne DATE arrive de
    // l'API en instant, et l'interpréter dépend du fuseau : l'important est que les
    // deux passent par la même transformation.
    const toDayInput = (value?: string | null) => (value ? format(new Date(value), 'yyyy-MM-dd') : '');

    const startEditingPeriod = () => {
        setStartDateInput(toDayInput(project?.startDate));
        setEndDateInput(toDayInput(project?.endDate));
        setPeriodError(null);
        setIsEditingPeriod(true);
    };

    const cancelEditingPeriod = () => {
        setIsEditingPeriod(false);
        setPeriodError(null);
    };

    const savePeriod = () => {
        const start = startDateInput.trim() || null;
        const end = endDateInput.trim() || null;

        if (start && end && end < start) {
            setPeriodError('La date de fin ne peut pas précéder la date de début.');
            return;
        }

        updatePeriodMutation.mutate({ startDate: start, endDate: end });
    };

    const startEditingBudget = () => {
        setBudgetDaysInput(project?.budgetHours ? String(hoursToDays(project.budgetHours)) : '');
        setBudgetError(null);
        setIsEditingBudget(true);
    };

    const cancelEditingBudget = () => {
        setIsEditingBudget(false);
        setBudgetError(null);
    };

    const saveBudget = () => {
        const trimmed = budgetDaysInput.trim();
        if (trimmed === '') {
            updateBudgetMutation.mutate(null);
            return;
        }
        const days = Number(trimmed.replace(',', '.'));
        if (Number.isNaN(days) || days < 0) {
            setBudgetError('Nombre de jours invalide');
            return;
        }
        updateBudgetMutation.mutate(daysToHours(days));
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
            </div>
        );
    }

    if (!project) {
        return (
            <div className="text-center py-12">
                <p className="text-[var(--color-text-muted)]">Projet non trouvé</p>
                <Link to="/projects" className="mt-4 text-[var(--color-primary)] hover:underline">
                    Retour aux projets
                </Link>
            </div>
        );
    }

    const budgetPercent = project.budgetHours
        ? Math.min((project.consumedHours || 0) / project.budgetHours * 100, 100)
        : 0;

    const budgetStatus =
        budgetPercent >= 90
            ? 'danger'
            : budgetPercent >= 75
                ? 'warning'
                : 'normal';

    // Somme des jours affectés aux consultants. Calculée ici plutôt que reprise du
    // total renvoyé par l'API, pour rester juste pendant qu'une saisie est en cours.
    const allocatedDays = (project.members || []).reduce(
        (sum, m) => sum + (m.allocatedDays ?? 0),
        0
    );

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                <Link
                    to="/projects"
                    className="p-2 hover:bg-[var(--color-bg)] rounded-lg transition-colors self-start"
                >
                    <ArrowLeft className="w-5 h-5" />
                </Link>

                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                        {isEditingCode ? (
                            <span className="inline-flex items-center gap-1">
                                <input
                                    type="text"
                                    value={codeInput}
                                    onChange={(e) => setCodeInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') saveCode();
                                        if (e.key === 'Escape') {
                                            setIsEditingCode(false);
                                            setCodeError(null);
                                        }
                                    }}
                                    autoFocus
                                    maxLength={50}
                                    className="input w-48 py-0.5 text-sm"
                                    aria-label="Code du projet"
                                />
                                <button
                                    type="button"
                                    onClick={saveCode}
                                    disabled={updateCodeMutation.isPending}
                                    className="p-1 text-[var(--color-success)] hover:bg-[var(--color-bg)] rounded"
                                    title="Enregistrer"
                                    aria-label="Enregistrer le code"
                                >
                                    {updateCodeMutation.isPending
                                        ? <Loader2 className="w-4 h-4 animate-spin" />
                                        : <Check className="w-4 h-4" />}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsEditingCode(false);
                                        setCodeError(null);
                                    }}
                                    className="p-1 text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] rounded"
                                    title="Annuler"
                                    aria-label="Annuler"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1">
                                <span className="px-2 py-0.5 bg-[var(--color-bg)] text-[var(--color-text-secondary)] text-sm font-medium rounded">
                                    {project.code}
                                </span>
                                {(isAdmin || isManager) && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setCodeInput(project.code);
                                            setIsEditingCode(true);
                                            setCodeError(null);
                                        }}
                                        className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                                        title={
                                            project.codeLocked
                                                ? 'Code fixé à la main : la synchronisation ne le change plus'
                                                : 'Modifier le code du projet'
                                        }
                                        aria-label="Modifier le code du projet"
                                    >
                                        <Pencil className="w-3 h-3" />
                                    </button>
                                )}
                            </span>
                        )}
                        {/* Affiché à part quand il diffère du code : un projet peut avoir été
                            créé sans commande, puis rattaché à l'une d'elles. */}
                        {project.orderNumber && project.orderNumber !== project.code && (
                            <span
                                className="px-2 py-0.5 bg-[var(--color-bg)] text-[var(--color-text-secondary)] text-sm rounded"
                                title="Numéro de commande"
                            >
                                {project.orderNumber}
                            </span>
                        )}
                        {/* Projet interne : même gabarit que la pastille Dynamics juste en
                            dessous, les deux qualifiant la nature du projet. La règle vient de
                            l'API. Le code valant « Interne » signalait l'interne avant que la
                            notion existe : la pastille ferait alors doublon. */}
                        {project.internal && project.code.trim().toLowerCase() !== 'interne' && (
                            <span
                                className="px-2 py-0.5 bg-[var(--color-primary-light)] text-[var(--color-primary)] text-xs rounded"
                                title="Projet interne à l'entreprise"
                            >
                                Interne
                            </span>
                        )}
                        {project.source === 'dynamics' && (
                            <span
                                className="px-2 py-0.5 bg-[var(--color-primary-light)] text-[var(--color-primary)] text-xs rounded"
                                title="Projet issu d'une commande Dynamics 365"
                            >
                                Dynamics
                            </span>
                        )}
                        {!project.active && (
                            <span className="px-2 py-0.5 bg-[var(--color-bg-hover)] text-[var(--color-text-muted)] text-sm rounded">
                                Inactif
                            </span>
                        )}

                        {isEditingStatus ? (
                            <select
                                value={project.status}
                                onChange={(e) => updateStatusMutation.mutate(e.target.value)}
                                disabled={updateStatusMutation.isPending}
                                autoFocus
                                onBlur={() => setIsEditingStatus(false)}
                                className="input w-auto py-0.5 text-sm"
                                aria-label="Statut du projet"
                            >
                                {/* Le statut courant reste proposé même hors de la liste :
                                    « Terminé » vient de SharePoint et ne doit pas disparaître
                                    du sélecteur qui l'affiche. */}
                                {(PROJECT_STATUS_CHOICES as readonly string[]).includes(project.status)
                                    ? null
                                    : <option value={project.status}>{projectStatusLabel(project.status)}</option>}
                                {PROJECT_STATUS_CHOICES.map((status) => (
                                    <option key={status} value={status}>
                                        {status}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <span className="inline-flex items-center gap-1">
                                <span className={projectStatusBadgeClass(project.status)}>
                                    {project.closed && <Lock className="w-3 h-3" />}
                                    {projectStatusLabel(project.status)}
                                </span>
                                {(isAdmin || isManager) && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsEditingStatus(true);
                                            setStatusError(null);
                                        }}
                                        className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                                        title="Changer le statut"
                                        aria-label="Changer le statut"
                                    >
                                        <Pencil className="w-3 h-3" />
                                    </button>
                                )}
                            </span>
                        )}
                        {updateStatusMutation.isPending && (
                            <Loader2 className="w-3 h-3 animate-spin text-[var(--color-primary)]" />
                        )}
                    </div>
                    {codeError && (
                        <p className="text-xs text-[var(--color-error)] mb-1">{codeError}</p>
                    )}
                    {statusError && (
                        <p className="text-xs text-[var(--color-error)] mb-1">{statusError}</p>
                    )}
                    <h1 className="text-2xl font-bold text-[var(--color-text)]">{project.name}</h1>
                    {project.clientName && (
                        <div className="flex items-center gap-1 text-[var(--color-text-muted)] mt-1">
                            <Building2 className="w-4 h-4" />
                            <span>{project.clientName}</span>
                        </div>
                    )}
                </div>

                {project.sharepointId && (
                    <a
                        href={`https://sharepoint.com/sites/projects/${project.sharepointId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg)] rounded-lg transition-colors"
                    >
                        <ExternalLink className="w-4 h-4" />
                        SharePoint
                    </a>
                )}
            </div>

            {/* Saisie fermée : dit une fois, en clair, plutôt que découvert au moment
                d'enregistrer une ligne. */}
            {project.closed && (
                <div className="card flex items-start gap-3 border-[var(--color-warning)]">
                    <Lock className="w-5 h-5 text-[var(--color-warning)] flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                        <p className="font-medium text-[var(--color-text)]">
                            La saisie est fermée sur ce projet
                        </p>
                        <p className="text-[var(--color-text-secondary)] mt-1">
                            Statut « {projectStatusLabel(project.status)} » : plus aucune heure ne peut
                            être ajoutée ni modifiée, et le projet ne figure plus dans les écrans de
                            saisie. Ce qui est déjà saisi reste en place et suit son cours de
                            validation.
                            {project.closedAt && (
                                <>
                                    {' '}Clôturé le{' '}
                                    {format(new Date(project.closedAt), 'd MMMM yyyy', { locale: fr })}
                                    {project.closedByName ? ` par ${project.closedByName}` : ''}.
                                </>
                            )}
                        </p>
                        {(isAdmin || isManager) && (
                            <p className="text-[var(--color-text-muted)] mt-1">
                                Repasser le projet « En cours » rouvre la saisie.
                            </p>
                        )}
                    </div>
                </div>
            )}

            {closingNotice !== null && (
                <div className="card flex items-start gap-3 border-[var(--color-primary)]">
                    <Clock className="w-5 h-5 text-[var(--color-primary)] flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                        <p className="font-medium text-[var(--color-text)]">
                            {closingNotice} saisie{closingNotice > 1 ? 's' : ''} en attente à la clôture
                        </p>
                        <p className="text-[var(--color-text-secondary)] mt-1">
                            Elles restent à valider ou à refuser : la clôture ferme la saisie, elle
                            ne solde pas ce qui a déjà été entré.
                        </p>
                    </div>
                </div>
            )}

            {/* Description */}
            {project.description && (
                <div className="card">
                    <div className="flex items-center gap-2 text-sm text-[var(--color-text-muted)] mb-2">
                        <FileText className="w-4 h-4" />
                        Description
                    </div>
                    <p className="text-[var(--color-text-secondary)]">{project.description}</p>
                </div>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Budget */}
                <div className="card">
                    <div className="flex items-center justify-between gap-2 text-sm text-[var(--color-text-muted)] mb-2">
                        <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            Budget
                        </div>
                        {isAdmin && !isEditingBudget && (
                            <button
                                type="button"
                                onClick={startEditingBudget}
                                className="p-1 hover:bg-[var(--color-bg)] rounded transition-colors"
                                title="Modifier le budget"
                            >
                                <Pencil className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                    {isEditingBudget ? (
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    min="0"
                                    step="0.5"
                                    value={budgetDaysInput}
                                    onChange={(e) => setBudgetDaysInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') saveBudget();
                                        if (e.key === 'Escape') cancelEditingBudget();
                                    }}
                                    placeholder="Nombre de jours"
                                    className="w-full px-2 py-1 text-lg font-semibold bg-[var(--color-bg)] border border-[var(--color-border)] rounded focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                                    autoFocus
                                    disabled={updateBudgetMutation.isPending}
                                />
                                <span className="text-[var(--color-text-muted)]">j</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={saveBudget}
                                    disabled={updateBudgetMutation.isPending}
                                    className="flex items-center gap-1 px-2 py-1 text-xs bg-[var(--color-primary)] text-white rounded hover:opacity-90 disabled:opacity-50"
                                >
                                    {updateBudgetMutation.isPending ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                        <Check className="w-3 h-3" />
                                    )}
                                    Enregistrer
                                </button>
                                <button
                                    type="button"
                                    onClick={cancelEditingBudget}
                                    disabled={updateBudgetMutation.isPending}
                                    className="flex items-center gap-1 px-2 py-1 text-xs bg-[var(--color-bg)] text-[var(--color-text-secondary)] rounded hover:bg-[var(--color-bg-hover)] disabled:opacity-50"
                                >
                                    <X className="w-3 h-3" />
                                    Annuler
                                </button>
                            </div>
                            {budgetError && (
                                <p className="text-xs text-[var(--color-error)]">{budgetError}</p>
                            )}
                            <p className="text-xs text-[var(--color-text-muted)]">
                                Laisser vide pour supprimer le budget. 1j = 8h.
                            </p>
                        </div>
                    ) : project.budgetHours ? (
                        <>
                            <p
                                className={cn(
                                    'text-2xl font-bold',
                                    budgetStatus === 'danger' && 'text-[var(--color-error)]',
                                    budgetStatus === 'warning' && 'text-[var(--color-warning)]',
                                    budgetStatus === 'normal' && 'text-[var(--color-text)]'
                                )}
                            >
                                {hoursToDays(project.consumedHours || 0)}j
                                <span className="text-[var(--color-text-muted)] font-normal text-lg">
                                    {' '}/ {hoursToDays(project.budgetHours)}j
                                </span>
                            </p>
                            <div className="mt-2 h-2 bg-[var(--color-bg)] rounded-full overflow-hidden">
                                <div
                                    className={cn(
                                        'h-full rounded-full transition-all',
                                        budgetStatus === 'danger' && 'bg-[var(--color-error)]',
                                        budgetStatus === 'warning' && 'bg-[var(--color-warning)]',
                                        budgetStatus === 'normal' && 'bg-[var(--color-primary)]'
                                    )}
                                    style={{ width: `${budgetPercent}%` }}
                                />
                            </div>
                        </>
                    ) : (
                        <p className="text-[var(--color-text-muted)] italic">Non défini</p>
                    )}
                </div>

                {/* Dates */}
                <div className="card">
                    <div className="flex items-center justify-between gap-2 text-sm text-[var(--color-text-muted)] mb-2">
                        <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            Période
                        </div>
                        {(isAdmin || isManager) && !isEditingPeriod && (
                            <button
                                type="button"
                                onClick={startEditingPeriod}
                                className="p-1 hover:bg-[var(--color-bg)] rounded transition-colors"
                                title="Modifier la période"
                            >
                                <Pencil className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                    {isEditingPeriod ? (
                        <div className="space-y-2">
                            <label className="block">
                                <span className="text-xs text-[var(--color-text-muted)]">Début</span>
                                <input
                                    type="date"
                                    value={startDateInput}
                                    onChange={(e) => setStartDateInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') savePeriod();
                                        if (e.key === 'Escape') cancelEditingPeriod();
                                    }}
                                    className="w-full px-2 py-1 text-sm bg-[var(--color-bg)] border border-[var(--color-border)] rounded focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                                    autoFocus
                                    disabled={updatePeriodMutation.isPending}
                                />
                            </label>
                            <label className="block">
                                <span className="text-xs text-[var(--color-text-muted)]">Fin</span>
                                <input
                                    type="date"
                                    value={endDateInput}
                                    min={startDateInput || undefined}
                                    onChange={(e) => setEndDateInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') savePeriod();
                                        if (e.key === 'Escape') cancelEditingPeriod();
                                    }}
                                    className="w-full px-2 py-1 text-sm bg-[var(--color-bg)] border border-[var(--color-border)] rounded focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                                    disabled={updatePeriodMutation.isPending}
                                />
                            </label>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={savePeriod}
                                    disabled={updatePeriodMutation.isPending}
                                    className="flex items-center gap-1 px-2 py-1 text-xs bg-[var(--color-primary)] text-white rounded hover:opacity-90 disabled:opacity-50"
                                >
                                    {updatePeriodMutation.isPending ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                        <Check className="w-3 h-3" />
                                    )}
                                    Enregistrer
                                </button>
                                <button
                                    type="button"
                                    onClick={cancelEditingPeriod}
                                    disabled={updatePeriodMutation.isPending}
                                    className="flex items-center gap-1 px-2 py-1 text-xs bg-[var(--color-bg)] text-[var(--color-text-secondary)] rounded hover:bg-[var(--color-bg-hover)] disabled:opacity-50"
                                >
                                    <X className="w-3 h-3" />
                                    Annuler
                                </button>
                            </div>
                            {periodError && (
                                <p className="text-xs text-[var(--color-error)]">{periodError}</p>
                            )}
                            <p className="text-xs text-[var(--color-text-muted)]">
                                Laisser vide pour retirer une date.{' '}
                                {/* Les deux synchronisations n'ont pas la même autorité sur les
                                    dates : SharePoint les réécrit depuis la liste, Dynamics ne
                                    les pose que si elles manquent. Le dire plutôt que de laisser
                                    une correction disparaître au passage suivant. */}
                                {project.source === 'sharepoint'
                                    ? 'La synchronisation SharePoint les réécrit ensuite depuis la liste : corriger la date là-bas pour qu\'elle tienne.'
                                    : 'La synchronisation ne réécrit pas une date déjà renseignée.'}
                            </p>
                        </div>
                    ) : project.startDate || project.endDate ? (
                        <div className="space-y-1">
                            {project.startDate && (
                                <p className="text-[var(--color-text-secondary)]">
                                    <span className="text-[var(--color-text-muted)]">Début:</span>{' '}
                                    {format(new Date(project.startDate), 'd MMMM yyyy', { locale: fr })}
                                </p>
                            )}
                            {project.endDate && (
                                <p className="text-[var(--color-text-secondary)]">
                                    <span className="text-[var(--color-text-muted)]">Fin:</span>{' '}
                                    {format(new Date(project.endDate), 'd MMMM yyyy', { locale: fr })}
                                </p>
                            )}
                        </div>
                    ) : (
                        <p className="text-[var(--color-text-muted)] italic">Non défini</p>
                    )}
                </div>

                {/* Approved Hours */}
                <div className="card">
                    <div className="flex items-center gap-2 text-sm text-[var(--color-text-muted)] mb-2">
                        <TrendingUp className="w-4 h-4" />
                        Jours validés
                    </div>
                    <p className="text-2xl font-bold text-[var(--color-success)]">
                        {hoursToDays(project.stats?.approvedHours || 0)}j
                    </p>
                </div>

                {/* Team Size */}
                <div className="card">
                    <div className="flex items-center gap-2 text-sm text-[var(--color-text-muted)] mb-2">
                        <Users className="w-4 h-4" />
                        Équipe
                    </div>
                    <p className="text-2xl font-bold text-[var(--color-text)]">
                        {project.members?.length || 0}
                    </p>
                    <p className="text-sm text-[var(--color-text-muted)]">
                        {allocatedDays > 0
                            ? `${formatDayCount(allocatedDays)}j affecté(s)`
                            : 'membre(s)'}
                    </p>
                </div>
            </div>

            {/* Consultants affectés, avec leur nombre de jours */}
            <ConsultantsCard
                projectId={project.id}
                members={project.members || []}
                soldDays={project.soldDays ?? null}
                canManage={isManager || isAdmin}
            />

            {/* CDP (Chefs de projet) */}
            <CdpCard projectId={project.id} cdps={project.cdps || []} canManage={isManager || isAdmin} />

            {/* Rattachement à une commande du CRM */}
            {isAdmin && (
                <DynamicsLinkCard
                    projectId={project.id}
                    dynamicsId={project.dynamicsId ?? null}
                    orderNumber={project.orderNumber ?? null}
                    sharedWith={project.sharedWith ?? []}
                />
            )}

            {/* Détail de la commande Dynamics */}
            <OrderLinesCard lines={project.orderLines || []} orderNumber={project.orderNumber ?? null} />

            {/* Recent Entries (Manager/Admin only) */}
            {(isManager || isAdmin) && project.recentEntries && project.recentEntries.length > 0 && (
                <div className="card !p-0 overflow-hidden">
                    <div className="p-4 border-b border-[var(--color-border)]">
                        <h2 className="font-semibold text-[var(--color-text)]">Saisies récentes</h2>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[640px]">
                            <thead className="bg-[var(--color-bg)] text-left">
                                <tr>
                                    <th className="px-4 py-3 text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">
                                        Collaborateur
                                    </th>
                                    <th className="px-4 py-3 text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">
                                        Date
                                    </th>
                                    <th className="px-4 py-3 text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">
                                        Jours
                                    </th>
                                    <th className="px-4 py-3 text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">
                                        Statut
                                    </th>
                                    <th className="px-4 py-3 text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">
                                        Description
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[var(--color-border)]">
                                {project.recentEntries.map((entry) => (
                                    <tr key={entry.id} className="hover:bg-[var(--color-bg)]">
                                        <td className="px-4 py-3 text-sm text-[var(--color-text)]">
                                            {entry.userName}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-[var(--color-text-muted)]">
                                            {format(new Date(entry.entryDate), 'd MMM yyyy', { locale: fr })}
                                        </td>
                                        <td className="px-4 py-3 text-sm font-medium text-[var(--color-text)]">
                                            {hoursToDays(entry.hours)}j
                                        </td>
                                        <td className="px-4 py-3">
                                            <StatusBadge status={entry.status} />
                                        </td>
                                        <td className="px-4 py-3 text-sm text-[var(--color-text-muted)] max-w-xs truncate">
                                            {entry.description || '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {isAdmin && project.deletion && (
                <DeleteProjectCard project={project} deletion={project.deletion} />
            )}
        </div>
    );
}

/** Formate un nombre de JOURS (à ne pas confondre avec `formatDays`, qui prend des heures). */
function formatDayCount(days: number): string {
    return String(Math.round(days * 100) / 100);
}

interface ConsultantsCardProps {
    projectId: string;
    members: ProjectMember[];
    /** Jours vendus sur la commande, pour situer le total affecté. */
    soldDays: number | null;
    canManage: boolean;
}

/**
 * Affectation des consultants au projet et nombre de jours de chacun.
 *
 * C'est ici que se gère désormais l'équipe, et non plus dans la liste SharePoint :
 * la synchronisation des projets n'écrase plus ces lignes dès que Dynamics alimente
 * les commandes.
 */
function ConsultantsCard({ projectId, members, soldDays, canManage }: ConsultantsCardProps) {
    const api = useApi();
    const queryClient = useQueryClient();
    const [selectedUserId, setSelectedUserId] = useState('');
    const [newDays, setNewDays] = useState('');
    const [drafts, setDrafts] = useState<Record<string, string>>({});
    const [error, setError] = useState<string | null>(null);

    const { data: usersData } = useQuery({
        queryKey: ['users', { active: true }],
        queryFn: () => api.get<ApiResponse<User[]>>('/api/users', { active: true }),
        enabled: canManage,
    });

    const saveMutation = useMutation({
        mutationFn: (payload: { userId: string; allocatedDays: number | null }) =>
            api.post<ApiResponse<ProjectMember>>(`/api/projects/${projectId}/members`, payload),
        onSuccess: (_data, payload) => {
            queryClient.invalidateQueries({ queryKey: ['project', projectId] });
            queryClient.invalidateQueries({ queryKey: ['projects'] });
            setSelectedUserId('');
            setNewDays('');
            // On oublie le brouillon de la ligne enregistrée, les autres restent.
            setDrafts((prev) => {
                const next = { ...prev };
                delete next[payload.userId];
                return next;
            });
            setError(null);
        },
        onError: (err: Error) => setError(err.message || "Erreur lors de l'affectation"),
    });

    const removeMutation = useMutation({
        mutationFn: (userId: string) =>
            api.delete<ApiResponse<unknown>>(`/api/projects/${projectId}/members/${userId}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['project', projectId] });
            queryClient.invalidateQueries({ queryKey: ['projects'] });
            setError(null);
        },
        onError: (err: Error) => setError(err.message || 'Erreur lors du retrait du consultant'),
    });

    if (!canManage && members.length === 0) return null;

    const memberUserIds = new Set(members.map((m) => m.userId));
    const availableUsers = (usersData?.data || []).filter((u) => u.active && !memberUserIds.has(u.id));

    const allocated = members.reduce((sum, m) => sum + (m.allocatedDays ?? 0), 0);
    const remaining = soldDays !== null ? Math.round((soldDays - allocated) * 100) / 100 : null;

    // Un nombre de jours saisi en français peut porter une virgule.
    const parseDays = (raw: string): number | null | undefined => {
        const trimmed = raw.trim();
        if (trimmed === '') return null;
        const value = Number(trimmed.replace(',', '.'));
        return Number.isFinite(value) && value >= 0 ? value : undefined;
    };

    const submitDays = (userId: string, raw: string) => {
        const parsed = parseDays(raw);
        if (parsed === undefined) {
            setError('Nombre de jours invalide');
            return;
        }
        saveMutation.mutate({ userId, allocatedDays: parsed });
    };

    const draftFor = (member: ProjectMember) =>
        drafts[member.userId] ?? (member.allocatedDays !== null && member.allocatedDays !== undefined
            ? String(member.allocatedDays)
            : '');

    return (
        <div className="card !p-0 overflow-hidden">
            <div className="p-4 border-b border-[var(--color-border)]">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-[var(--color-text-muted)]" />
                        <h2 className="font-semibold text-[var(--color-text)]">Consultants affectés</h2>
                    </div>
                    <div className="text-sm text-[var(--color-text-muted)]">
                        {formatDayCount(allocated)}j affecté(s)
                        {soldDays !== null && <> sur {formatDayCount(soldDays)}j vendu(s)</>}
                        {remaining !== null && remaining !== 0 && (
                            <span
                                className={cn(
                                    'ml-2',
                                    remaining < 0 ? 'text-[var(--color-error)]' : 'text-[var(--color-text-secondary)]'
                                )}
                            >
                                {remaining > 0
                                    ? `${formatDayCount(remaining)}j à affecter`
                                    : `${formatDayCount(Math.abs(remaining))}j en trop`}
                            </span>
                        )}
                    </div>
                </div>
                <p className="text-sm text-[var(--color-text-muted)] mt-1">
                    L'affectation se gère ici. La synchronisation des projets ne la modifie pas.
                </p>
            </div>

            {members.length > 0 ? (
                <div className="divide-y divide-[var(--color-border)]">
                    {members.map((member) => {
                        const draft = draftFor(member);
                        const stored = member.allocatedDays !== null && member.allocatedDays !== undefined
                            ? String(member.allocatedDays)
                            : '';
                        const changed = draft.trim().replace(',', '.') !== stored;

                        return (
                            <div key={member.userId} className="flex flex-wrap items-center justify-between gap-3 p-4">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-10 h-10 rounded-full bg-[var(--color-primary-light)] flex items-center justify-center flex-shrink-0">
                                        <span className="text-[var(--color-primary)] font-medium">
                                            {member.displayName
                                                .split(' ')
                                                .map((n: string) => n[0])
                                                .join('')
                                                .toUpperCase()
                                                .slice(0, 2)}
                                        </span>
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-medium text-[var(--color-text)] truncate">
                                            {member.displayName}
                                        </p>
                                        <p className="text-sm text-[var(--color-text-muted)] truncate">
                                            {member.email}
                                        </p>
                                    </div>
                                </div>

                                {canManage ? (
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.5"
                                            value={draft}
                                            onChange={(e) =>
                                                setDrafts((prev) => ({ ...prev, [member.userId]: e.target.value }))
                                            }
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') submitDays(member.userId, draft);
                                            }}
                                            placeholder="jours"
                                            aria-label={`Jours affectés à ${member.displayName}`}
                                            className="input w-24"
                                        />
                                        <span className="text-sm text-[var(--color-text-muted)]">j</span>
                                        <button
                                            type="button"
                                            onClick={() => submitDays(member.userId, draft)}
                                            disabled={!changed || saveMutation.isPending}
                                            className="p-2 text-[var(--color-text-muted)] hover:text-[var(--color-success)] hover:bg-[var(--color-bg)] rounded-lg transition-colors disabled:opacity-40"
                                            title="Enregistrer le nombre de jours"
                                        >
                                            <Check className="w-4 h-4" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => removeMutation.mutate(member.userId)}
                                            disabled={removeMutation.isPending}
                                            className="p-2 text-[var(--color-text-muted)] hover:text-[var(--color-error)] hover:bg-[var(--color-bg)] rounded-lg transition-colors disabled:opacity-50"
                                            title="Retirer ce consultant"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <span className="text-sm text-[var(--color-text-secondary)]">
                                        {member.allocatedDays !== null && member.allocatedDays !== undefined
                                            ? `${formatDayCount(member.allocatedDays)}j`
                                            : '—'}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="p-4 text-sm text-[var(--color-text-muted)] italic">
                    Aucun consultant affecté à ce projet.
                </div>
            )}

            {canManage && (
                <div className="p-4 border-t border-[var(--color-border)] bg-[var(--color-bg)]">
                    <div className="flex flex-col sm:flex-row gap-2">
                        <select
                            value={selectedUserId}
                            onChange={(e) => setSelectedUserId(e.target.value)}
                            className="input flex-1"
                        >
                            <option value="">Sélectionner un consultant…</option>
                            {availableUsers.map((u) => (
                                <option key={u.id} value={u.id}>
                                    {u.displayName} ({u.email})
                                </option>
                            ))}
                        </select>
                        <input
                            type="number"
                            min="0"
                            step="0.5"
                            value={newDays}
                            onChange={(e) => setNewDays(e.target.value)}
                            placeholder="jours"
                            aria-label="Nombre de jours à affecter"
                            className="input w-full sm:w-28"
                        />
                        <button
                            type="button"
                            onClick={() => selectedUserId && submitDays(selectedUserId, newDays)}
                            disabled={!selectedUserId || saveMutation.isPending}
                            className="btn-primary justify-center"
                        >
                            {saveMutation.isPending ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <UserPlus className="w-4 h-4" />
                            )}
                            Affecter
                        </button>
                    </div>
                    {error && <p className="text-xs text-[var(--color-error)] mt-2">{error}</p>}
                </div>
            )}
        </div>
    );
}

interface CdpCardProps {
    projectId: string;
    cdps: ProjectCdp[];
    canManage: boolean;
}

function CdpCard({ projectId, cdps, canManage }: CdpCardProps) {
    const api = useApi();
    const queryClient = useQueryClient();
    const [selectedUserId, setSelectedUserId] = useState('');
    const [error, setError] = useState<string | null>(null);

    // Liste des utilisateurs (uniquement si l'on peut gérer les CDP)
    const { data: usersData } = useQuery({
        queryKey: ['users', { active: true }],
        queryFn: () => api.get<ApiResponse<User[]>>('/api/users', { active: true }),
        enabled: canManage,
    });

    const addMutation = useMutation({
        mutationFn: (userId: string) =>
            api.post<ApiResponse<ProjectCdp>>(`/api/projects/${projectId}/cdps`, { userId }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['project', projectId] });
            queryClient.invalidateQueries({ queryKey: ['projects'] });
            setSelectedUserId('');
            setError(null);
        },
        onError: (err: Error) => setError(err.message || "Erreur lors de l'ajout du CDP"),
    });

    const removeMutation = useMutation({
        mutationFn: (userId: string) =>
            api.delete<ApiResponse<unknown>>(`/api/projects/${projectId}/cdps/${userId}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['project', projectId] });
            queryClient.invalidateQueries({ queryKey: ['projects'] });
            setError(null);
        },
        onError: (err: Error) => setError(err.message || 'Erreur lors du retrait du CDP'),
    });

    // Masquer entièrement la carte si aucun CDP et pas de droit de gestion
    if (!canManage && cdps.length === 0) return null;

    const cdpUserIds = new Set(cdps.map((c) => c.userId));
    const availableUsers = (usersData?.data || []).filter(
        (u) => u.active && !cdpUserIds.has(u.id)
    );

    return (
        <div className="card !p-0 overflow-hidden">
            <div className="p-4 border-b border-[var(--color-border)]">
                <div className="flex items-center gap-2">
                    <UserCog className="w-4 h-4 text-[var(--color-text-muted)]" />
                    <h2 className="font-semibold text-[var(--color-text)]">Chefs de projet (CDP)</h2>
                </div>
                <p className="text-sm text-[var(--color-text-muted)] mt-1">
                    Les CDP accèdent, dans Rapports → « Projets pilotés », à la consommation globale de ce projet
                    et à la répartition par collaborateur.
                </p>
            </div>

            {/* Liste des CDP */}
            {cdps.length > 0 ? (
                <div className="divide-y divide-[var(--color-border)]">
                    {cdps.map((cdp) => (
                        <div key={cdp.userId} className="flex items-center justify-between p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-[var(--color-primary-light)] flex items-center justify-center">
                                    <span className="text-[var(--color-primary)] font-medium">
                                        {cdp.displayName
                                            .split(' ')
                                            .map((n) => n[0])
                                            .join('')
                                            .toUpperCase()
                                            .slice(0, 2)}
                                    </span>
                                </div>
                                <div>
                                    <p className="font-medium text-[var(--color-text)]">{cdp.displayName}</p>
                                    <p className="text-sm text-[var(--color-text-muted)]">{cdp.email}</p>
                                </div>
                            </div>
                            {canManage && (
                                <button
                                    type="button"
                                    onClick={() => removeMutation.mutate(cdp.userId)}
                                    disabled={removeMutation.isPending}
                                    className="p-2 text-[var(--color-text-muted)] hover:text-[var(--color-error)] hover:bg-[var(--color-bg)] rounded-lg transition-colors disabled:opacity-50"
                                    title="Retirer ce CDP"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="p-4 text-sm text-[var(--color-text-muted)] italic">
                    Aucun CDP désigné pour ce projet.
                </div>
            )}

            {/* Ajout d'un CDP (admin / manager) */}
            {canManage && (
                <div className="p-4 border-t border-[var(--color-border)] bg-[var(--color-bg)]">
                    <div className="flex flex-col sm:flex-row gap-2">
                        <select
                            value={selectedUserId}
                            onChange={(e) => setSelectedUserId(e.target.value)}
                            className="input flex-1"
                        >
                            <option value="">Sélectionner un collaborateur…</option>
                            {availableUsers.map((u) => (
                                <option key={u.id} value={u.id}>
                                    {u.displayName} ({u.email})
                                </option>
                            ))}
                        </select>
                        <button
                            type="button"
                            onClick={() => selectedUserId && addMutation.mutate(selectedUserId)}
                            disabled={!selectedUserId || addMutation.isPending}
                            className="btn-primary justify-center"
                        >
                            {addMutation.isPending ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <UserPlus className="w-4 h-4" />
                            )}
                            Ajouter
                        </button>
                    </div>
                    {error && <p className="text-xs text-[var(--color-error)] mt-2">{error}</p>}
                </div>
            )}
        </div>
    );
}

interface SalesOrderSummary {
    salesOrderId: string;
    orderNumber: string | null;
    name: string | null;
    customer: string | null;
    soldDays: number | null;
    /** Projets déjà rattachés à cette commande. */
    linkedProjects: Array<{ id: string; code: string }>;
}

/**
 * Rattachement du projet à une commande du CRM.
 *
 * Sert à trois choses : rattacher un projet créé à la main, corriger un rattachement, et
 * rattacher plusieurs projets à une même commande lorsqu'un lot de jours se découpe en
 * chantiers suivis séparément.
 *
 * Sur une commande rattachée à un seul projet, la synchronisation reprend le nom, le
 * client et les jours vendus de la commande. Sur une commande partagée, elle laisse le nom
 * et les jours en place : la répartition est une décision humaine.
 */
function DynamicsLinkCard({
    projectId,
    dynamicsId,
    orderNumber,
    sharedWith,
}: {
    projectId: string;
    dynamicsId: string | null;
    orderNumber: string | null;
    sharedWith: Array<{ id: string; code: string; name: string }>;
}) {
    const api = useApi();
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const [results, setResults] = useState<SalesOrderSummary[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    const searchMutation = useMutation({
        mutationFn: (term: string) =>
            api.get<ApiResponse<SalesOrderSummary[]>>('/api/sync/dynamics/orders', { search: term }),
        onSuccess: (res) => {
            setResults(res.data ?? []);
            setError(null);
        },
        onError: (err: Error) => {
            setResults(null);
            setError(err.message || 'Recherche impossible');
        },
    });

    const linkMutation = useMutation({
        mutationFn: (salesOrderId: string) =>
            api.post<ApiResponse<unknown>>(`/api/projects/${projectId}/dynamics-link`, { salesOrderId }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['project', projectId] });
            queryClient.invalidateQueries({ queryKey: ['projects'] });
            setResults(null);
            setSearch('');
            setError(null);
        },
        onError: (err: Error) => setError(err.message || 'Rattachement impossible'),
    });

    const unlinkMutation = useMutation({
        mutationFn: () => api.delete<ApiResponse<unknown>>(`/api/projects/${projectId}/dynamics-link`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['project', projectId] });
            queryClient.invalidateQueries({ queryKey: ['projects'] });
            setError(null);
        },
        onError: (err: Error) => setError(err.message || 'Détachement impossible'),
    });

    return (
        <div className="card !p-0 overflow-hidden">
            <div className="p-4 border-b border-[var(--color-border)]">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <div className="flex items-center gap-2">
                            <Link2 className="w-4 h-4 text-[var(--color-text-muted)]" />
                            <h2 className="font-semibold text-[var(--color-text)]">Commande Dynamics</h2>
                        </div>
                        <p className="text-sm text-[var(--color-text-muted)] mt-1">
                            {dynamicsId
                                ? sharedWith.length > 0
                                    ? `Rattaché à ${orderNumber ?? 'une commande'}, partagée avec ${sharedWith.length} autre(s) projet(s). Les jours vendus de la commande ne sont pas répartis automatiquement : le budget de chaque projet se règle à la main.`
                                    : `Rattaché à ${orderNumber ?? 'une commande'}. La synchronisation reprend le nom, le client et les jours vendus depuis la commande.`
                                : 'Ce projet n’est rattaché à aucune commande. Le rattacher permet de reprendre le client, les jours vendus et le détail des lignes.'}
                        </p>
                        {sharedWith.length > 0 && (
                            <ul className="mt-2 space-y-0.5">
                                {sharedWith.map((other) => (
                                    <li key={other.id} className="text-sm">
                                        <Link
                                            to={`/projects/${other.id}`}
                                            className="text-[var(--color-primary)] hover:underline"
                                        >
                                            {other.code}
                                        </Link>{' '}
                                        <span className="text-[var(--color-text-muted)]">{other.name}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    {dynamicsId && (
                        <button
                            type="button"
                            onClick={() => unlinkMutation.mutate()}
                            disabled={unlinkMutation.isPending}
                            className="btn-secondary"
                        >
                            {unlinkMutation.isPending ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Link2Off className="w-4 h-4" />
                            )}
                            Détacher
                        </button>
                    )}
                </div>
            </div>

            <div className="p-4 space-y-3">
                <div className="flex flex-col sm:flex-row gap-2">
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') searchMutation.mutate(search.trim());
                        }}
                        placeholder="Numéro de commande ou intitulé…"
                        className="input flex-1"
                    />
                    <button
                        type="button"
                        onClick={() => searchMutation.mutate(search.trim())}
                        disabled={searchMutation.isPending}
                        className="btn-secondary justify-center"
                    >
                        {searchMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Search className="w-4 h-4" />
                        )}
                        Chercher
                    </button>
                </div>
                <p className="text-xs text-[var(--color-text-muted)]">
                    Recherche vide pour voir les commandes les plus récentes. Le filtre sur le type de
                    service configuré en administration s'applique aussi ici.
                </p>

                {error && <p className="text-sm text-[var(--color-error)] break-words">{error}</p>}

                {results && results.length === 0 && (
                    <p className="text-sm text-[var(--color-text-muted)] italic">
                        Aucune commande ne correspond.
                    </p>
                )}

                {results && results.length > 0 && (
                    <div className="divide-y divide-[var(--color-border)] rounded-lg border border-[var(--color-border)]">
                        {results.map((order) => {
                            const isCurrent = order.salesOrderId === dynamicsId;
                            // Rattacher une commande déjà portée par d'autres projets est
                            // permis, mais on l'annonce.
                            const others = order.linkedProjects.filter((l) => l.id !== projectId);

                            return (
                                <div
                                    key={order.salesOrderId}
                                    className="flex flex-wrap items-center justify-between gap-2 p-3"
                                >
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-[var(--color-text)] truncate">
                                            {order.orderNumber ?? 'Sans numéro'} · {order.name ?? 'Sans intitulé'}
                                        </p>
                                        <p className="text-xs text-[var(--color-text-muted)] truncate">
                                            {order.customer ?? 'Client inconnu'}
                                            {order.soldDays !== null && ` · ${order.soldDays}j vendus`}
                                            {others.length > 0 &&
                                                ` · déjà rattachée à ${others.map((o) => o.code).join(', ')}`}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => linkMutation.mutate(order.salesOrderId)}
                                        disabled={linkMutation.isPending || isCurrent}
                                        className="btn-secondary"
                                        title={
                                            isCurrent
                                                ? 'Commande déjà rattachée à ce projet'
                                                : others.length > 0
                                                    ? 'Rattacher aussi ce projet à cette commande'
                                                    : 'Rattacher cette commande'
                                        }
                                    >
                                        <Link2 className="w-4 h-4" />
                                        {isCurrent ? 'Rattachée' : others.length > 0 ? 'Rattacher aussi' : 'Rattacher'}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * Détail de la commande : une ligne par produit, telles qu'elles figurent dans
 * Dynamics. Rien n'est modifiable ici, la commande fait autorité et les lignes sont
 * remplacées à chaque synchronisation.
 */
function OrderLinesCard({
    lines,
    orderNumber,
}: {
    lines: ProjectOrderLine[];
    orderNumber: string | null;
}) {
    // Rien à montrer pour un projet sans commande, ou créé avant cette reprise.
    if (lines.length === 0) return null;

    const totalQuantity = lines.reduce((sum, l) => sum + (l.quantity ?? 0), 0);

    return (
        <div className="card !p-0 overflow-hidden">
            <div className="p-4 border-b border-[var(--color-border)]">
                <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-[var(--color-text-muted)]" />
                    <h2 className="font-semibold text-[var(--color-text)]">
                        Détail de la commande
                        {orderNumber && (
                            <span className="font-normal text-[var(--color-text-muted)]"> · {orderNumber}</span>
                        )}
                    </h2>
                </div>
                <p className="text-sm text-[var(--color-text-muted)] mt-1">
                    Lignes issues de Dynamics, en lecture seule.
                </p>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full min-w-[520px]">
                    <thead className="bg-[var(--color-bg)] text-left">
                        <tr>
                            <th className="px-4 py-3 text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">
                                Produit
                            </th>
                            <th className="px-4 py-3 text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">
                                Description
                            </th>
                            <th className="px-4 py-3 text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider text-right">
                                Quantité
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--color-border)]">
                        {lines.map((line) => (
                            <tr key={line.id}>
                                <td className="px-4 py-3 text-sm text-[var(--color-text)]">
                                    {line.productName || <span className="text-[var(--color-text-muted)]">—</span>}
                                </td>
                                <td className="px-4 py-3 text-sm text-[var(--color-text-secondary)]">
                                    {line.description || <span className="text-[var(--color-text-muted)]">—</span>}
                                </td>
                                <td className="px-4 py-3 text-sm text-[var(--color-text)] text-right whitespace-nowrap">
                                    {line.quantity !== null && line.quantity !== undefined
                                        ? formatDayCount(line.quantity)
                                        : '—'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    {/* Total affiché dès qu'il y a plus d'une ligne : c'est lui qui
                        alimente les jours vendus du projet. */}
                    {lines.length > 1 && (
                        <tfoot className="bg-[var(--color-bg)]">
                            <tr>
                                <td className="px-4 py-3 text-sm font-medium text-[var(--color-text)]" colSpan={2}>
                                    Total
                                </td>
                                <td className="px-4 py-3 text-sm font-medium text-[var(--color-text)] text-right">
                                    {formatDayCount(totalQuantity)}
                                </td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </div>
    );
}

function StatusBadge({ status }: { status: string }) {
    const config = {
        draft: { label: 'Brouillon', classes: 'badge-draft' },
        submitted: { label: 'Soumis', classes: 'badge-submitted' },
        approved: { label: 'Validé', classes: 'badge-approved' },
        rejected: { label: 'Rejeté', classes: 'badge-rejected' },
        billed: { label: 'Facturé', classes: 'badge-billed' },
    }[status] || { label: status, classes: 'badge-draft' };

    return (
        <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', config.classes)}>
            {config.label}
        </span>
    );
}

interface DeleteProjectCardProps {
    project: ProjectDetailData;
    deletion: ProjectDeletionImpact;
}

/**
 * Suppression du projet, réservée aux administrateurs.
 *
 * Un geste sans retour, et la page qui le porte disparaît avec lui : la carte annonce donc
 * d'abord ce qui partirait, puis demande de retaper le code du projet. Recopier un code
 * demande de lire ce qu'on s'apprête à détruire, ce qu'une case à cocher n'obtient pas.
 *
 * Quand la suppression est impossible, la carte explique pourquoi et propose la clôture,
 * qui ferme la saisie sans rien détruire.
 */
function DeleteProjectCard({ project, deletion }: DeleteProjectCardProps) {
    const api = useApi();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [isConfirming, setIsConfirming] = useState(false);
    const [codeTyped, setCodeTyped] = useState('');
    const [error, setError] = useState<string | null>(null);

    const deleteMutation = useMutation({
        mutationFn: () => api.delete<ApiResponse<{ code: string; comesBack: boolean }>>(`/api/projects/${project.id}`),
        onSuccess: (res) => {
            // La fiche n'a plus de sujet : on retire sa requête du cache plutôt que de
            // l'invalider, sinon react-query la redemanderait aussitôt pour un 404.
            queryClient.removeQueries({ queryKey: ['project', project.id], exact: true });
            queryClient.invalidateQueries({ queryKey: ['projects'] });
            queryClient.invalidateQueries({ queryKey: ['stats'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'duplicates'] });
            navigate('/projects', {
                replace: true,
                state: { deletedProject: { code: res.data?.code ?? project.code, comesBack: res.data?.comesBack ?? false } },
            });
        },
        onError: (err: Error) => setError(err.message || 'La suppression a échoué'),
    });

    const parts = [
        deletion.members > 0 && `${deletion.members} affectation${deletion.members > 1 ? 's' : ''} de consultant`,
        deletion.cdps > 0 && `${deletion.cdps} chef${deletion.cdps > 1 ? 's' : ''} de projet`,
        deletion.orderLines > 0 && `${deletion.orderLines} ligne${deletion.orderLines > 1 ? 's' : ''} de commande`,
        deletion.notifications > 0 && `${deletion.notifications} notification${deletion.notifications > 1 ? 's' : ''}`,
    ].filter(Boolean) as string[];

    return (
        <div className="card border-[var(--color-error)]">
            <div className="flex items-center gap-2 text-sm font-medium text-[var(--color-error)] mb-3">
                <Trash2 className="w-4 h-4" />
                Supprimer le projet
            </div>

            {deletion.blockedReason ? (
                <p className="text-sm text-[var(--color-text-secondary)]">{deletion.blockedReason}</p>
            ) : (
                <>
                    <p className="text-sm text-[var(--color-text-secondary)]">
                        {parts.length > 0
                            ? `Partiraient avec le projet : ${parts.join(', ')}.`
                            : 'Ce projet ne porte ni affectation, ni chef de projet, ni ligne de commande.'}
                        {' '}Aucune saisie n'y est rattachée. La suppression est définitive.
                    </p>
                    {deletion.comesBack && (
                        <p className="text-sm text-[var(--color-warning)] mt-2">
                            Attention : ce projet vient d'une source externe. Tant que sa ligne existe
                            dans SharePoint ou que sa commande existe dans Dynamics, il sera recréé dans
                            les minutes qui suivent, sans ses affectations ni ses chefs de projet, et
                            avec un statut neuf. Pour l'écarter durablement, passez-le en « Clôturé »
                            plutôt que de le supprimer.
                        </p>
                    )}

                    {isConfirming ? (
                        <div className="mt-4 space-y-2">
                            <label className="block text-sm text-[var(--color-text-secondary)]">
                                Retapez le code <span className="font-medium text-[var(--color-text)]">{project.code}</span> pour confirmer
                            </label>
                            <div className="flex flex-wrap items-center gap-2">
                                <input
                                    type="text"
                                    value={codeTyped}
                                    onChange={(e) => setCodeTyped(e.target.value)}
                                    autoFocus
                                    className="input w-56"
                                    aria-label="Code du projet à confirmer"
                                />
                                <button
                                    type="button"
                                    onClick={() => deleteMutation.mutate()}
                                    disabled={codeTyped !== project.code || deleteMutation.isPending}
                                    className="px-3 py-2 rounded-lg text-sm text-white bg-[var(--color-error)] disabled:opacity-50"
                                >
                                    {deleteMutation.isPending
                                        ? <Loader2 className="w-4 h-4 animate-spin" />
                                        : 'Supprimer définitivement'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsConfirming(false);
                                        setCodeTyped('');
                                        setError(null);
                                    }}
                                    className="btn-secondary"
                                >
                                    Annuler
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setIsConfirming(true)}
                            className="mt-4 px-3 py-2 rounded-lg text-sm border border-[var(--color-error)] text-[var(--color-error)] hover:bg-[var(--color-error-light)]"
                        >
                            <Trash2 className="w-4 h-4 inline mr-1 -mt-0.5" />
                            Supprimer ce projet
                        </button>
                    )}
                </>
            )}

            {error && <p className="text-sm text-[var(--color-error)] mt-2">{error}</p>}
        </div>
    );
}
